import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector, useAuth } from '../hooks';

import { DocumentActions } from '../actions/document/document.actions';

import type { Document as BsmDocument } from '../interfaces/document/document.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import FileIcon from './components/FileIcon';
import EditPencil from './components/EditPencil';
import { fileExtension, formatBytes, deriveAccess } from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';
import FilterMenu, { applyFilters } from './components/FilterMenu';

import DocumentEditModal from './DocumentEditModal';

// =============================================================================
//  Documents (firm-wide library) — v3 redesign
//
//  Layout differs from the other list pages: 220px left rail with category
//  filters + Quick access section, table on the right.
//
//  Available on the back-end:    id, name, document_type (contract / info /
//                                 presentation), category (denormalised string),
//                                 sub_category, user (uploader full name),
//                                 created_at, data[] (attached files with
//                                 name + url + uploaded date)
//  Stubbed:                      Access level (no RBAC column),
//                                 Size      (no byte-size in API),
//                                 Quick-access lists (Pinned / Recent / Shared)
//                                 — Quick-access shown as toggle stubs only
// =============================================================================

const QUICK_ACCESS = ['Pinned by you', 'Recent', 'Shared with me'] as const;

const DocumentsListV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const documents: BsmDocument[] =
    useAppSelector(({ document_store }) => document_store.documents) || [];

  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [extraFilters, setExtraFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    dispatch(DocumentActions.loadDocuments(noOp));
  }, [dispatch]);

  // Compute category counts dynamically from real data.  The design's preset
  // list (Templates / Policies / Compliance / Methodology / Brand / HR) does
  // not match what's actually in the BSM staging DB, so we render whatever
  // categories exist — fall back gracefully when documents are empty.
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    documents.forEach((d) => {
      const cat = d.category || 'Uncategorised';
      counts.set(cat, (counts.get(cat) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [documents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((d) => {
      if (categoryFilter !== 'All' && (d.category || 'Uncategorised') !== categoryFilter)
        return false;
      if (q) {
        const hay = `${d.name ?? ''} ${d.category ?? ''} ${d.user ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [documents, categoryFilter, search]);

  // The left rail filters by category; this adds the other axes.
  const FILTER_FIELDS = useMemo(
    () => [
      { key: 'type', label: 'Document type', accessor: (d: BsmDocument) => d.document_type },
      { key: 'owner', label: 'Uploaded by', accessor: (d: BsmDocument) => d.user },
      { key: 'sub', label: 'Sub-category', accessor: (d: BsmDocument) => d.sub_category }
    ],
    []
  );
  const filteredPlus = useMemo(
    () => applyFilters(filtered, FILTER_FIELDS, extraFilters),
    [filtered, FILTER_FIELDS, extraFilters]
  );

  const SORT_ACCESSORS = useMemo(
    () => ({
      type: (d: BsmDocument) => d.document_type,
      document: (d: BsmDocument) => d.name,
      category: (d: BsmDocument) => d.category,
      owner: (d: BsmDocument) => d.user,
      access: (d: BsmDocument) => deriveAccess(d),
      updated: (d: BsmDocument) => (d.created_at ? new Date(d.created_at as string) : null)
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filteredPlus, SORT_ACCESSORS);

  const lastUpdated = useMemo(() => {
    let latest = '';
    documents.forEach((d) => {
      const v = String(d.created_at ?? '');
      if (v > latest) latest = v;
    });
    return latest;
  }, [documents]);

  return (
    <>
      <Topbar
        crumbs={['Firm', 'Company Documents']}
        tools={
          <FilterMenu<BsmDocument>
            rows={filtered}
            fields={FILTER_FIELDS}
            value={extraFilters}
            onChange={setExtraFilters}
          />
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Firm Knowledge</div>
            <h1 className="page-title">Company documents</h1>
            <div className="page-sub">
              {documents.length} document{documents.length === 1 ? '' : 's'}
              {lastUpdated && ` · last updated ${lastUpdated}`}
            </div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search documents…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            {/* Upload still uses the legacy form, which lives under the admin
                route tree and is wrapped in AdminRoute.  Only render it for
                admins — otherwise the click bounces to /dashboard with a
                "not authorized" notice. */}
            {isAdmin && (
              <button
                className="btn primary"
                type="button"
                onClick={() => navigate('/admin/documents/add')}
                title="Opens the legacy upload form (file picker, category, sub-category)"
              >
                <Icon name="upload" size={14} />
                Upload
              </button>
            )}
          </div>
        </div>

        <div className="docs">
          {/* --- Left rail --------------------------------------------- */}
          <aside className="docs-rail">
            <div className="group">
              <div className="group-title">Categories</div>
              <button
                type="button"
                className={`rail-item ${categoryFilter === 'All' ? 'active' : ''}`}
                onClick={() => setCategoryFilter('All')}
              >
                <span>All</span>
                <span className="count">{documents.length}</span>
              </button>
              {categories.map(({ name, count }) => (
                <button
                  key={name}
                  type="button"
                  className={`rail-item ${categoryFilter === name ? 'active' : ''}`}
                  onClick={() => setCategoryFilter(name)}
                >
                  <span>{name}</span>
                  <span className="count">{count}</span>
                </button>
              ))}
            </div>

            <div className="rail-divider" />

            <div className="group">
              <div className="group-title">Quick access</div>
              {QUICK_ACCESS.map((q) => (
                // Stubbed for now — the v3 pilot does not have pinning, recents or sharing.
                <button
                  key={q}
                  type="button"
                  className="quick-link"
                  title="Not implemented yet"
                  onClick={(e) => e.preventDefault()}
                >
                  {q === 'Pinned by you' && <Icon name="pin" size={13} />}
                  {q === 'Recent' && <Icon name="clock" size={13} />}
                  {q === 'Shared with me' && <Icon name="users" size={13} />}
                  {q}
                </button>
              ))}
            </div>
          </aside>

          {/* --- Right pane ------------------------------------------- */}
          <div className="card flush">
            {filtered.length === 0 ? (
              <div className="muted" style={{ padding: 48, textAlign: 'center', fontSize: 13.5 }}>
                {documents.length === 0 ? 'No documents yet.' : 'No documents match these filters.'}
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <SortableTh label="Type" sortKey="type" sort={sort} onToggle={toggle} />
                    <SortableTh label="Document" sortKey="document" sort={sort} onToggle={toggle} />
                    <SortableTh label="Category" sortKey="category" sort={sort} onToggle={toggle} />
                    <SortableTh label="Owner" sortKey="owner" sort={sort} onToggle={toggle} />
                    <SortableTh label="Access" sortKey="access" sort={sort} onToggle={toggle} />
                    <th className="right">Size</th>
                    <th>Updated</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((d) => {
                    const file = Array.isArray(d.data) && d.data.length > 0 ? d.data[0] : undefined;
                    const filename = (file && (file as { name?: string }).name) || `${d.name}.bin`;
                    const ext = fileExtension(filename) || 'BIN';
                    return (
                      <tr
                        key={d.id}
                        onClick={() => navigate(`/documents/${d.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <FileIcon filename={filename} />
                        </td>
                        <td>
                          <div className="file-cell">
                            <div className="name">
                              <span className="title">{d.name}</span>
                              <span className="sub">{ext}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          {d.category ? <span className="chip muted">{d.category}</span> : '—'}
                        </td>
                        <td>{d.user || '—'}</td>
                        <td>{deriveAccess(d) ?? <span className="muted">Not set</span>}</td>
                        <td className="num right">{formatBytes(file?.byte_size)}</td>
                        <td className="mono">{String(d.created_at ?? '—')}</td>
                        <td className="actions-cell">
                          <EditPencil
                            onClick={() => setEditingId(d.id as number | string)}
                            ariaLabel={`Edit ${d.name}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {editingId !== null &&
        (() => {
          const d = filtered.find((x) => String(x.id) === String(editingId));
          if (!d) return null;
          return <DocumentEditModal document={d} onClose={() => setEditingId(null)} />;
        })()}
    </>
  );
};

export default DocumentsListV3;
